import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import ajaxAccounting, { ajaxItems } from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const emptyForm = () => ({
  code: "", name: "", category: "expense", description: "",
  default_account_id: "", default_price: "", default_tax_rate_id: "",
  track_inventory: false, reorder_level: "",
});

const PurchaseItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [assetAccounts, setAssetAccounts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchItems(); fetchMeta(); }, [lowStockOnly]); // eslint-disable-line

  const fetchItems = async () => {
    setLoading(true);
    const res = lowStockOnly
      ? await ajaxItems.getLowStockPurchaseItems()
      : await ajaxItems.listPurchaseItems({});
    if (res.status === "OK") setItems(Array.isArray(res.details) ? res.details : []);
    else if (res.status !== "Fail") toast.error(res.message);
    else setItems([]);
    setLoading(false);
  };

  const fetchMeta = async () => {
    const [exp, ast, tax] = await Promise.all([
      ajaxAccounting.listAccounts({ type: "expense", postable_only: 1 }),
      ajaxAccounting.listAccounts({ type: "asset", postable_only: 1 }),
      ajaxAccounting.listTaxes(),
    ]);
    if (exp.status === "OK") setExpenseAccounts(exp.details || []);
    if (ast.status === "OK") setAssetAccounts(ast.details || []);
    if (tax.status === "OK") setTaxRates(tax.details || []);
  };

  const acctOptions = (list) => list.map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }));
  const allAccountOptions = acctOptions([...expenseAccounts, ...assetAccounts]);
  const taxOptions = [{ value: "", label: "No tax" }, ...taxRates.map((t) => ({ value: String(t.id), label: `${t.name} (${t.rate}%)` }))];
  const categoryOptions = [
    { value: "expense", label: "Expense" },
    { value: "service", label: "Service" },
    { value: "product", label: "Product (raw material / stock)" },
  ];

  const buildPayload = (f) => ({
    code: f.code, name: f.name, category: f.category, description: f.description,
    default_account_id: f.default_account_id ? parseInt(f.default_account_id) : null,
    default_price: parseFloat(f.default_price) || 0,
    default_tax_rate_id: f.default_tax_rate_id ? parseInt(f.default_tax_rate_id) : null,
    track_inventory: f.category === "product" && f.track_inventory ? 1 : 0,
    reorder_level: f.category === "product" && f.track_inventory && f.reorder_level !== "" ? parseFloat(f.reorder_level) : null,
  });

  const handleCreate = async () => {
    if (!form.code || !form.name || !form.default_account_id) { toast.error("Code, name, and account are required."); return; }
    setSaving(true);
    const res = await ajaxItems.createPurchaseItem(buildPayload(form));
    if (res.status === "OK") {
      toast.success("Item created!");
      fetchItems();
      setShowModal(false);
      setForm(emptyForm());
    } else toast.error(res.message);
    setSaving(false);
  };

  const openEdit = (row) => setEditModal({
    id: row.id, code: row.code, name: row.name, category: row.category, description: row.description || "",
    default_account_id: String(row.default_account_id), default_price: row.default_price,
    default_tax_rate_id: row.default_tax_rate_id ? String(row.default_tax_rate_id) : "",
    track_inventory: !!row.track_inventory, reorder_level: row.reorder_level ?? "",
  });

  const handleUpdate = async () => {
    setSaving(true);
    const res = await ajaxItems.updatePurchaseItem({ id: editModal.id, ...buildPayload(editModal) });
    if (res.status === "OK") { toast.success("Item updated!"); fetchItems(); setEditModal(null); }
    else toast.error(res.message);
    setSaving(false);
  };

  const handleAdjust = async () => {
    if (!adjustDelta) { toast.error("Enter a quantity change."); return; }
    setSaving(true);
    const res = await ajaxItems.adjustPurchaseItemStock({ id: adjustModal.id, delta: parseFloat(adjustDelta), reason: adjustReason });
    if (res.status === "OK") { toast.success("Stock adjusted!"); fetchItems(); setAdjustModal(null); setAdjustDelta(""); setAdjustReason(""); }
    else toast.error(res.message);
    setSaving(false);
  };

  const columns = [
    { key: "code", label: "Code", render: (v) => <code>{v}</code> },
    { key: "name", label: "Name", render: (v) => <strong>{v}</strong> },
    { key: "category", label: "Category", render: (v) => <span className={`badge bg-${v === "product" ? "primary" : v === "service" ? "info" : "secondary"} text-capitalize`}>{v}</span> },
    { key: "default_price", label: "Price", render: (v) => functions.formatCurrency(v) },
    {
      key: "quantity_on_hand", label: "Stock", render: (v, row) => !row.track_inventory ? "—" :
        <span className={row.reorder_level != null && v <= row.reorder_level ? "tx-danger fw-bold" : ""}>{v}</span>,
    },
    {
      key: "id", label: "Actions",
      render: (id, row) => (
        <div className="d-flex gap-1">
          {row.track_inventory && (
            <button className="btn btn-sm btn-light" title="Adjust Stock" onClick={() => setAdjustModal(row)}><i className="fa-solid fa-boxes-stacked"></i></button>
          )}
          <button className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => openEdit(row)}><i className="fa-solid fa-pen"></i></button>
        </div>
      ),
    },
  ];

  return (
    <AppContainer
      title="Purchase Items"
      previousPageTitle="Accounting"
      description="Reusable catalog for purchase invoice lines. Mark a 'product' item as tracking stock to receive raw materials/inventory into stock automatically when a purchase invoice is approved."
      actions={[{ label: "New Item", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => setShowModal(true) }]}
    >
      <Toaster />
      <div className="card custom-card p-3 mb-3">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="lowStockOnly" checked={lowStockOnly} onChange={(e) => setLowStockOnly(e.target.checked)} />
          <label className="form-check-label tx-13" htmlFor="lowStockOnly">Show only items at or below reorder level</label>
        </div>
      </div>
      <div className="card custom-card p-3">
        <DataTable columns={columns} data={items} loading={loading} pageSize={15} emptyMessage="No items found." />
      </div>

      {(showModal || editModal) && (
        <AppModal
          title={<>{editModal ? "Edit Item" : "New Item"}</>}
          size="lg"
          onClose={() => { setShowModal(false); setEditModal(null); }}
          footer={
            <>
              <button className="btn btn-light" onClick={() => { setShowModal(false); setEditModal(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={editModal ? handleUpdate : handleCreate} disabled={saving}>
                {saving ? "Saving..." : editModal ? "Save Changes" : "Create Item"}
              </button>
            </>
          }
        >
          <ItemForm
            f={editModal || form}
            setF={editModal ? setEditModal : setForm}
            isEdit={!!editModal}
            categoryOptions={categoryOptions}
            allAccountOptions={allAccountOptions}
            taxOptions={taxOptions}
          />
        </AppModal>
      )}

      {adjustModal && (
        <AppModal
          title={<>Adjust Stock — {adjustModal.name}</>}
          onClose={() => setAdjustModal(null)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setAdjustModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdjust} disabled={saving}>{saving ? "Saving..." : "Apply Adjustment"}</button>
            </>
          }
        >
          <p className="tx-13 tx-muted">Current balance: <strong>{adjustModal.quantity_on_hand}</strong></p>
          <label className="form-label tx-12 fw-semibold">Quantity Change <span className="tx-danger">*</span></label>
          <input type="number" className="form-control mb-3" placeholder="e.g. 50 or -10" value={adjustDelta} onChange={(e) => setAdjustDelta(e.target.value)} />
          <label className="form-label tx-12 fw-semibold">Reason</label>
          <input className="form-control" placeholder="e.g. Stocktake correction" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
        </AppModal>
      )}
    </AppContainer>
  );
};

const ItemForm = ({ f, setF, isEdit, categoryOptions, allAccountOptions, taxOptions }) => (
  <div className="row g-3">
    <div className="col-lg-4">
      <label className="form-label tx-12 fw-semibold">Code <span className="tx-danger">*</span></label>
      <input className="form-control" value={f.code} disabled={isEdit} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="e.g. STEEL-SHEET" />
    </div>
    <div className="col-lg-8">
      <label className="form-label tx-12 fw-semibold">Name <span className="tx-danger">*</span></label>
      <input className="form-control" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
    </div>
    <div className="col-lg-4">
      <label className="form-label tx-12 fw-semibold">Category <span className="tx-danger">*</span></label>
      <Select2 options={categoryOptions} value={f.category} onChange={(v) => setF({ ...f, category: v })} />
    </div>
    <div className="col-lg-4">
      <label className="form-label tx-12 fw-semibold">Default Price</label>
      <input type="number" className="form-control" value={f.default_price} onChange={(e) => setF({ ...f, default_price: e.target.value })} />
    </div>
    <div className="col-lg-4">
      <label className="form-label tx-12 fw-semibold">Default Tax Rate</label>
      <Select2 options={taxOptions} value={f.default_tax_rate_id} onChange={(v) => setF({ ...f, default_tax_rate_id: v })} />
    </div>
    <div className="col-12">
      <label className="form-label tx-12 fw-semibold">Posting Account <span className="tx-danger">*</span></label>
      <Select2 options={allAccountOptions} value={f.default_account_id} onChange={(v) => setF({ ...f, default_account_id: v })}
        placeholder="Expense account, or an asset/inventory account if this stocks inventory" />
    </div>
    <div className="col-12">
      <label className="form-label tx-12 fw-semibold">Description</label>
      <textarea className="form-control" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
    </div>
    {f.category === "product" && (
      <>
        <div className="col-12">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="trackInventory" checked={f.track_inventory} onChange={(e) => setF({ ...f, track_inventory: e.target.checked })} />
            <label className="form-check-label tx-13" htmlFor="trackInventory">Track stock quantity for this item (posting account must be an asset/inventory account)</label>
          </div>
        </div>
        {f.track_inventory && (
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Reorder Level</label>
            <input type="number" className="form-control" value={f.reorder_level} onChange={(e) => setF({ ...f, reorder_level: e.target.value })} placeholder="Alert when stock falls to/below this" />
          </div>
        )}
      </>
    )}
  </div>
);

export default PurchaseItems;
